import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { Logo } from "@/components/Logo";

const ENTITY = "Strict RR";
const ADDRESS = "Bangalore, Karnataka, India";
export const SUPPORT_EMAIL = "support@strictrr.com";

const Page = ({ title, updated = "June 2026", testid, children }) => {
  useEffect(() => {
    document.title = `${title} · Strict RR — Risk Management Software`;
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div className="min-h-screen bg-[#08090A] text-zinc-100 antialiased">
      <header className="sticky top-0 z-50 border-b border-zinc-900/80 bg-black/70 backdrop-blur-xl">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 h-14 flex items-center gap-3">
          <Link to="/" data-testid="legal-home-link">
            <Logo size={26} />
          </Link>
          <div className="flex-1" />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] font-semibold text-zinc-400 hover:text-zinc-100 transition-colors duration-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        </div>
      </header>

      <main
        className="max-w-[900px] mx-auto px-5 sm:px-8 py-12 sm:py-16"
        data-testid={testid}
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-500">Legal</p>
        <h1 className="mt-4 text-3xl sm:text-4xl font-heading font-bold tracking-tight">{title}</h1>
        <p className="mt-3 font-mono text-[11px] text-zinc-600">Last updated: {updated}</p>
        <div className="mt-10 space-y-8 text-sm leading-relaxed text-zinc-400">{children}</div>
      </main>

      <SiteFooter />
    </div>
  );
};

const H = ({ children }) => (
  <h2 className="text-base md:text-lg font-heading font-semibold tracking-tight text-zinc-100">
    {children}
  </h2>
);

const Block = ({ title, children }) => (
  <section className="space-y-3">
    <H>{title}</H>
    {children}
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-4">
    {items.map((t) => (
      <li key={t} className="list-disc marker:text-emerald-600">
        {t}
      </li>
    ))}
  </ul>
);

export const RiskDisclosure = () => (
  <Page title="Risk disclosure" testid="risk-page">
    <Block title="Strict RR is software, not advice">
      <p>
        Strict RR is a risk-management and trade-execution tool that connects to a MetaTrader 5
        account you already own at a provider you already chose. We are not a broker, dealer, fund,
        portfolio manager, investment adviser or financial planner, and we are not licensed as any of
        these.
      </p>
      <p>
        We do not provide trading signals, buy or sell calls, tips, market forecasts, mirrored or
        replicated trades,
        managed accounts or personalised investment advice. Every order placed through Strict RR is
        an order that you decided to place. Nothing on this website or inside the product should be
        read as a recommendation to buy or sell any instrument.
      </p>
    </Block>

    <Block title="No promise of profit or income">
      <p>
        We make no representation, projection or guarantee about profit, income, returns, win rate,
        drawdown or account growth. Any figure, screenshot, chart or example shown anywhere on this
        site is illustrative or historical and must not be taken as a forecast. Past performance,
        yours or anyone else's, is not indicative of future results.
      </p>
    </Block>

    <Block title="You can lose money">
      <p>
        Participating in global financial markets carries a high
        level of risk and is not suitable for everyone. Losses can build faster than you expect.
        You can lose some, all or, with some providers, more than your deposited capital. Never trade
        with money you cannot afford to lose.
      </p>
      <Bullets
        items={[
          "Markets can gap, slip or become illiquid, so a stop loss may fill at a worse price than requested.",
          "Execution depends on your execution provider, your MetaTrader 5 terminal, your VPS and your internet connection — all outside our control.",
          "Software, hosting, tunnels and third-party APIs can fail, delay or disconnect. Risk rules cannot be enforced while a connection is down.",
          "Risk limits reduce the chance of a very large loss; they cannot eliminate losses or guarantee any outcome.",
        ]}
      />
    </Block>

    <Block title="Your responsibility">
      <p>
        You remain the account holder and the sole decision maker. You are responsible for your
        strategy, your position sizes, your provider relationship, your tax obligations and for
        complying with the laws of your jurisdiction. If you are unsure whether trading is
        appropriate for you, seek advice from an independent, licensed professional.
      </p>
    </Block>

    <Block title="Questions">
      <p>
        Write to us at{" "}
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </Block>
  </Page>
);

export const Privacy = () => (
  <Page title="Privacy policy" testid="privacy-page">
    <Block title="Who we are">
      <p>
        Strict RR ("we", "us") is operated by {ENTITY}, {ADDRESS}. This policy explains what personal
        data we collect through strictrr.com and the Strict RR application, why we collect it, and
        the choices you have. For any privacy question or request, contact{" "}
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </Block>

    <Block title="Data we collect">
      <Bullets
        items={[
          "Application details you submit voluntarily: name, email address, phone number with country code, and your answers to the qualification questions (such as experience, execution provider, instruments traded and what you would like to improve).",
          "Account data when you are invited to the platform: your Google account email and name (via Google sign-in), your role, and session records.",
          "Trading configuration and activity inside the product: MetaTrader 5 login and server, your risk settings, orders, positions, trade history and alerts generated by the rules engine.",
          "Technical data: IP-derived country used only to pre-select your phone country code, browser and device information, and error or access logs.",
        ]}
      />
      <p>
        We do not collect payment card data on our servers. We do not knowingly collect data from
        anyone under 18, and the service is not intended for minors.
      </p>
    </Block>

    <Block title="Why we use it">
      <Bullets
        items={[
          "To review your application, contact you and schedule an introduction call.",
          "To create and secure your account and authenticate your sessions.",
          "To operate the product: enforce your risk rules, place and manage orders you request, and show your history and alerts.",
          "To support you, fix faults, prevent abuse and keep records required by law.",
          "To improve the product using aggregated, non-identifying usage information.",
        ]}
      />
      <p>
        We rely on your consent for the application form, on contract performance for operating your
        account, and on our legitimate interest for security, fraud prevention and product
        improvement. We do not sell your personal data, and we do not use your trading data to trade
        against you.
      </p>
    </Block>

    <Block title="Sharing and processors">
      <p>
        We share data only with service providers that help us run the platform, under confidentiality
        obligations: cloud hosting and database providers, Google (sign-in), our scheduling provider
        for booking calls, and network/tunnel providers used to reach your MetaTrader 5 terminal. We
        may disclose data where required by law or to protect our rights.
      </p>
    </Block>

    <Block title="Advertising and cookies">
      <p>
        We use strictly necessary cookies and local browser storage to keep you signed in. If we run
        advertising on platforms such as Meta or Google, those platforms may set cookies or use
        pixels on our marketing pages to measure whether an ad led to an application. You can control
        this through your browser settings and through the ad-preference controls of the platform you
        saw the ad on. Blocking non-essential cookies does not affect your ability to use the
        product.
      </p>
    </Block>

    <Block title="Retention and security">
      <p>
        We keep application data for up to 24 months from your last contact with us, and account and
        trading records for as long as your account is active plus any period required by law. Access
        is restricted, traffic is encrypted in transit, and credentials required to reconnect your
        terminal are stored on our servers with restricted access. No system is perfectly secure, so
        please use a strong, unique password at your execution provider and enable its security features.
      </p>
    </Block>

    <Block title="Your rights">
      <p>
        Depending on where you live, you may ask us to access, correct, export or delete your
        personal data, to restrict or object to processing, or to withdraw consent. Email{" "}
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{" "}
        and we will respond within 30 days. Deleting your account removes your access and your
        trading records from the product, subject to legal retention duties. To have your application
        removed before we ever speak, email us with the subject "Delete my application".
      </p>
    </Block>

    <Block title="Changes">
      <p>
        We will update this page when our practices change and revise the date above. Continued use
        after a change means you accept the updated policy.
      </p>
    </Block>
  </Page>
);

export const Terms = () => (
  <Page title="Terms of service" testid="terms-page">
    <Block title="1. About these terms">
      <p>
        These terms are an agreement between you and {ENTITY} ({ADDRESS}), the operator of Strict RR.
        By using strictrr.com, submitting an application or using the platform, you accept these
        terms and our{" "}
        <Link className="text-emerald-400 hover:text-emerald-300" to="/privacy">
          privacy policy
        </Link>{" "}
        and{" "}
        <Link className="text-emerald-400 hover:text-emerald-300" to="/risk-disclosure">
          risk disclosure
        </Link>
        . If you do not accept them, do not use the service.
      </p>
    </Block>

    <Block title="2. What the service is and is not">
      <p>
        Strict RR is software that enforces risk rules and routes orders you place to your own
        MetaTrader 5 account. We are not a broker, exchange, fund, money manager or financial
        adviser. We do not hold, receive or transfer your funds, we do not provide signals or
        investment advice, and we do not trade on your behalf. All trading decisions are yours.
      </p>
    </Block>

    <Block title="3. Eligibility and access">
      <p>
        Access is invite only. You must be at least 18 years old, legally able to enter this
        agreement, and permitted to trade the relevant instruments in your jurisdiction. We may
        approve, decline, suspend or withdraw access at our discretion, including if you breach these
        terms or attempt to bypass risk controls.
      </p>
    </Block>

    <Block title="4. Your account and credentials">
      <p>
        You are responsible for the accuracy of the account credentials you provide, for keeping your
        sign-in secure, and for every action taken under your account. Tell us immediately if you
        suspect unauthorised access. You agree not to reverse engineer, resell, sublicense or
        interfere with the service, and not to use it for unlawful activity or market abuse.
      </p>
    </Block>

    <Block title="5. Risk rules">
      <p>
        Risk settings agreed with you are enforced on our side before an order reaches your execution provider.
        Rules may block your orders — that is the intended behaviour. Enforcement depends on
        connectivity to your terminal and on your provider's execution; while a connection is down we
        cannot enforce anything, and positions already open at your execution provider remain your
        responsibility.
      </p>
    </Block>

    <Block title="6. Fees">
      <p>
        Any fee, billing cycle and currency are the ones agreed with you in writing before access is
        granted. Fees are for use of the software only and are never a share of, or a claim on, your
        trading results. Applying and speaking to us costs nothing.
      </p>
    </Block>

    <Block title="7. Availability">
      <p>
        We aim for high availability but do not guarantee uninterrupted service. Maintenance,
        third-party outages, provider downtime, network failure or force majeure can interrupt access.
        We may change or discontinue features with reasonable notice.
      </p>
    </Block>

    <Block title="8. No warranty and limitation of liability">
      <p>
        The service is provided "as is" without warranties of any kind, express or implied, including
        fitness for a particular purpose. To the fullest extent permitted by law, we are not liable
        for trading losses, lost profits, missed opportunities, data loss or indirect or
        consequential damages arising from your use of the service. Where liability cannot be
        excluded, it is limited to the fees you paid us in the three months before the claim.
      </p>
    </Block>

    <Block title="9. Termination">
      <p>
        You may stop using the service and ask us to close your account at any time. We may terminate
        access for breach of these terms or for unlawful use. On termination your access ends;
        positions at your execution provider remain yours to manage.
      </p>
    </Block>

    <Block title="10. Changes, law and contact">
      <p>
        We may update these terms and will revise the date above. The agreement is governed by the
        laws applicable at our place of establishment, and disputes are subject to the courts there.
        Questions:{" "}
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </Block>
  </Page>
);

export const RefundPolicy = () => (
  <Page title="Refund and cancellation policy" testid="refund-page">
    <Block title="Applying is free">
      <p>
        Submitting an application and having an introduction call with us costs nothing. No payment is
        taken on this website, and we never ask for payment before we have spoken to you.
      </p>
    </Block>

    <Block title="What you pay for">
      <p>
        If you are invited to the desk, any access fee, its amount, currency and billing cycle are
        confirmed to you in writing before you pay. The fee covers use of the Strict RR software
        only. It is never a share of your trading results, and it is never a payment for advice,
        signals or account management.
      </p>
    </Block>

    <Block title="Cancellation">
      <p>
        You can cancel at any time by emailing{" "}
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        . Cancellation stops the next billing cycle. Your access continues until the end of the period
        you have already paid for, and no fee is charged after that.
      </p>
    </Block>

    <Block title="Refunds">
      <Bullets
        items={[
          "Request within 7 days of your first payment and you get a full refund, no questions asked.",
          "If a fault on our side leaves the platform unusable for more than 72 consecutive hours in a billing period, we refund that period on request.",
          "Part-used periods after the first 7 days are not refundable, unless local consumer law requires otherwise.",
          "Trading losses are never refundable — we do not control markets, your execution provider or your decisions.",
        ]}
      />
      <p>
        Approved refunds are returned to the original payment method within 5–10 business days,
        depending on your bank or payment provider.
      </p>
    </Block>

    <Block title="How to request">
      <p>
        Email{" "}
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{" "}
        from the address on your account with the subject "Refund request". We reply within 3 business
        days.
      </p>
    </Block>
  </Page>
);

export const Contact = () => (
  <Page title="Contact us" testid="contact-page">
    <Block title="Talk to a human">
      <p>
        We are a small desk, so you will be speaking to the people who built and run the platform.
        The fastest way to reach us is email.
      </p>
      <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-950/60 space-y-2 font-mono text-[12px]">
        <p>
          <span className="text-zinc-600">EMAIL — </span>
          <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
        <p>
          <span className="text-zinc-600">OPERATOR — </span>
          <span className="text-zinc-300">{ENTITY}</span>
        </p>
        <p>
          <span className="text-zinc-600">ADDRESS — </span>
          <span className="text-zinc-300">{ADDRESS}</span>
        </p>
        <p>
          <span className="text-zinc-600">HOURS — </span>
          <span className="text-zinc-300">Mon–Fri, 9:00–18:00 IST · reply within 1 business day</span>
        </p>
      </div>
    </Block>

    <Block title="Want access?">
      <p>
        Access is invite only. Start with the{" "}
        <Link className="text-emerald-400 hover:text-emerald-300" to="/#apply">
          application form
        </Link>{" "}
        and we will arrange a call. Applying is free and there is no obligation.
      </p>
    </Block>

    <Block title="What we cannot help with">
      <p>
        We cannot tell you what to buy or sell, give you market opinions, manage your account or
        recommend an execution provider for your circumstances. For questions about deposits, withdrawals,
        spreads, swaps or your account balance, contact your execution provider directly.
      </p>
    </Block>
  </Page>
);

export const About = () => (
  <Page title="About Strict RR" testid="about-page">
    <Block title="What we do">
      <p>
        Strict RR is trade-execution and risk-management software for people who already trade their
        own MetaTrader 5 account. You agree your limits once — risk per trade, daily loss limit,
        maximum trades, trading window, stop-loss rules — and the software holds you to them on every
        order. If a trade breaks what you agreed to, it does not go through.
      </p>
    </Block>

    <Block title="Why it exists">
      <p>
        Most trading plans do not fail on paper; they fail in the moment. The tool exists to remove
        the decision to break your own rule, not to tell you what to trade. Nothing about your
        strategy changes, and nothing about your provider relationship changes.
      </p>
    </Block>

    <Block title="What we are not">
      <Bullets
        items={[
          "Not a broker or an exchange — you keep your own execution provider and your own funds.",
          "Not a signal service, course or mentorship selling entries and exits.",
          "Not a fund or money manager — we never hold or move your money.",
          "Not a financial adviser — we give no investment advice and no performance promises.",
        ]}
      />
    </Block>

    <Block title="How we work">
      <p>
        The desk is deliberately small and invite only, because each trader gets an introduction call
        and their own execution slot. Applying is free. Read the{" "}
        <Link className="text-emerald-400 hover:text-emerald-300" to="/risk-disclosure">
          risk disclosure
        </Link>{" "}
        before you apply — active market participation carries a real risk of loss.
      </p>
    </Block>

    <Block title="Get in touch">
      <p>
        <a className="text-emerald-400 hover:text-emerald-300" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{" "}
        · {ENTITY}, {ADDRESS}
      </p>
    </Block>
  </Page>
);
